import { useState } from 'react';
import { Key, RefreshCw, Trash2 } from 'lucide-react';

import { env } from 'config/env';
import { maskApiKey, trackSegmentEvent } from 'app/utils';
import { useAppSelector, useAppDispatch } from 'app/store';
import { selectUserInfo } from 'app/store/user';
import { ACTION, SOURCE } from 'app/analytics';
import { regenerateApiKey, revokeSecondaryApiKey } from 'app/graphql/admin-api';
import { toast } from 'sonner';
import { canWrite } from 'app/access';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { CopyableField } from 'components/ui/copyable-field';
import { Button } from 'components/ui/button';
import { Typography } from 'components/ui/typography';

// Each row pairs the labelled key field with its action button so they
// share a baseline — denser than stacking the buttons in their own row.
interface ApiKeyRowProps {
  label: string;
  value: string;
  copyButtonTitle: string;
  onCopy?: () => void;
  action: React.ReactNode;
}

const ApiKeyRow = ({ label, value, copyButtonTitle, onCopy, action }: ApiKeyRowProps) => (
  <div className="flex items-end gap-2">
    <div className="min-w-0 flex-1">
      <Typography variant="label" as="p" className="mb-1">
        {label}
      </Typography>
      <CopyableField
        value={value}
        mask={maskApiKey}
        onCopy={onCopy}
        copyButtonTitle={copyButtonTitle}
      />
    </div>
    {action}
  </div>
);

export const ApiKeySection = () => {
  const dispatch = useAppDispatch();
  const userInfo = useAppSelector(selectUserInfo);
  const { apiKey = '', apiKeySecondary } = userInfo || {};
  const canManageCheckoutIntentKeys = canWrite(userInfo, 'checkout_intents');

  const [loading, setLoading] = useState(false);

  const isProd = env.VITE_NODE_ENV === 'production';

  const handleRotate = async () => {
    if (!canManageCheckoutIntentKeys) {
      return;
    }

    setLoading(true);
    try {
      const { data } = await regenerateApiKey();
      const result = data?.regenerateApiKey;
      if (result?.error) {
        toast.error(result.error);
      } else if (result?.data?.user) {
        dispatch({
          type: 'user/update',
          payload: {
            apiKey: result.data.user.apiKey,
            apiKeySecondary: result.data.user.apiKeySecondary ?? null,
          },
        });
        toast.success('API key rotated');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to rotate API key');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async () => {
    if (!canManageCheckoutIntentKeys) {
      return;
    }

    setLoading(true);
    try {
      const { data } = await revokeSecondaryApiKey();
      const result = data?.revokeSecondaryApiKey;
      if (result?.error) {
        toast.error(result.error);
      } else if (result?.data?.user) {
        dispatch({
          type: 'user/update',
          payload: {
            apiKey: result.data.user.apiKey,
            apiKeySecondary: result.data.user.apiKeySecondary ?? null,
          },
        });
        toast.success('Old key revoked');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to revoke old key');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <Key className="h-5 w-5" />
          <CardTitle>Your API Credentials</CardTitle>
        </div>
        <CardDescription>
          Use these credentials to authenticate your requests to{' '}
          {isProd ? 'the Rye API' : "Rye's Staging API"}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ApiKeyRow
          label="Primary Key"
          value={apiKey}
          copyButtonTitle="Copy Authorization token"
          onCopy={() =>
            trackSegmentEvent(SOURCE.API_KEY_MODULE, ACTION.CLICK, 'copy_dashbboard_api_key_button')
          }
          action={
            <Button
              variant="outline"
              disabled={loading || !canManageCheckoutIntentKeys}
              onClick={handleRotate}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Rotate
            </Button>
          }
        />

        {apiKeySecondary && (
          <ApiKeyRow
            label="Old Key (still active)"
            value={apiKeySecondary}
            copyButtonTitle="Copy old API key"
            action={
              <Button
                variant="destructive"
                disabled={loading || !canManageCheckoutIntentKeys}
                onClick={handleRevoke}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Revoke
              </Button>
            }
          />
        )}
      </CardContent>
    </Card>
  );
};
